import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  getIncomePointEdge,
  getStandardIncomePoints,
  getStandardPerfectRatio,
  INCOME_POINT_EDGE_KEY,
} from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import { Icon } from "@rentiohq/web-shared/dist/components";
import * as S from "@rentiohq/web-shared/dist/components/SharedForm/fields/IncomeRangesField/IncomeRangesField.styled";

/**
 * What an office is scored on while it has set nothing: the built-in scale,
 * written out at the same six rents the editable table starts from.
 *
 * Wears the editable table's own styling rather than a copy of it, so the two
 * cannot drift apart. Press Personalize and these lines become fields, with the
 * values unchanged: a table of points taken off a straight line reproduces that
 * line exactly, so nothing about the scoring moves.
 */
const formatRatio = (ratio: number) => ratio.toFixed(1).replace(".", ",");

export const StandardIncomeScale = () => (
  <>
    <S.Table>
      <S.HeadRow>
        <S.RentCell>
          {getLocalizedText("broker.rentmatch.income_ranges.column.rent")}
        </S.RentCell>
        <S.ScoreCell>
          {getLocalizedText("broker.rentmatch.income_ranges.column.income")}
        </S.ScoreCell>
      </S.HeadRow>

      {getStandardIncomePoints().map((point, index, points) => {
        const ratio = getStandardPerfectRatio(point.maxRent);
        const edge = getIncomePointEdge(index, points.length);

        return (
          <S.Row key={point.maxRent} $isOdd={index % 2 === 1}>
            <S.RentCell>
              {formatCurrency(point.maxRent, false)}
              {edge && (
                <S.Text>{getLocalizedText(INCOME_POINT_EDGE_KEY[edge])}</S.Text>
              )}
            </S.RentCell>
            <S.ScoreCell>
              <S.Strong>
                {getLocalizedText(
                  "broker.rentmatch.income_ranges.standard.row.factor",
                  { factor: formatRatio(ratio) },
                )}
              </S.Strong>
              <S.Amount>
                {getLocalizedText(
                  "broker.rentmatch.income_ranges.standard.row.amount",
                  {
                    amount: formatCurrency(
                      Math.round(point.maxRent * ratio),
                      false,
                    ),
                  },
                )}
              </S.Amount>
            </S.ScoreCell>
          </S.Row>
        );
      })}
    </S.Table>

    <S.Note>
      <S.NoteLabel>
        <Icon source="informationCircle" size="small" />
        {getLocalizedText("broker.rentmatch.income_ranges.between_rows")}
      </S.NoteLabel>
    </S.Note>
  </>
);
